import { Progress } from "./progress";
import { usePage } from "@tree-company/tree-query";
import clsx from "clsx";
import { useEffect, useRef } from "react";
import I18n from "../locales";

// Tracks embed hosts (<amai-embed>) that have already shown their first question.
// Using a WeakSet keyed on the host element means it survives PageComponent
// remounts (a new component instance per question) while still being GC-friendly.
const initializedEmbeds = new WeakSet();

function stepForPageKey(key) {
  if (key.startsWith("action") || key.startsWith("statement")) {
    return 1;
  } else if (key.startsWith("comment") || key === "name") {
    return 2;
  } else if (key === "sociodemo") {
    return 3;
  } else if (key === "like" || key === "optin") {
    return 4;
  }
}

export function PageComponent({
  handleSave,
  page,
  children,
  onPreviousPage,
  className,
  variant,
}) {
  const {
    title,
    description,
    htmlAttributes,
    key,
    disablePrevious,
    disableNext,
  } = usePage(page);
  const i18n = new I18n();
  const elementRef = useRef();
  const isInitialRender = useRef(true); // used for the non-embed (regular page) case

  // Make sure the browsers back button has the same effect as our back button
  useEffect(() => {
    // If the previous history entry was a string, we already track history within the form
    // in that case, we can use `replaceState`
    // If not, we need to use `pushState` so that we can distinguish our history states from react router dom
    if (typeof window.history.state === "string") {
      window.history.replaceState(key, "", window.location.toString());
    } else {
      window.history.pushState(key, "", window.location.toString());
    }

    const goBack = (event) => {
      event.preventDefault();
      onPreviousPage();
    };
    window.addEventListener("popstate", goBack, { once: true });
    return () => window.removeEventListener("popstate", goBack, { once: true });
  }, [key]);

  useEffect(() => {
    const locationWithKey = new URL(window.location);
    locationWithKey.pathname += `/${key}`;

    let timeoutId;
    let retryDelay = 50;
    let maxWaitMs = 1000;
    let startedAt = Date.now();

    function tryFathom() {
      if (!window.fathom) {
        let elapsed = Date.now() - startedAt;

        if (elapsed >= maxWaitMs) {
          return;
        }

        timeoutId = setTimeout(tryFathom, retryDelay);
        return;
      }

      window.fathom.trackPageview({
        url: locationWithKey.toString(),
      });
    }

    tryFathom();

    return () => clearTimeout(timeoutId);
  }, [key]);

  useEffect(() => {
    if (!elementRef.current) return;

    const root = elementRef.current.getRootNode();
    const host = root instanceof ShadowRoot ? root.host : null;

    if (host) {
      // Embed (Shadow DOM): PageComponent remounts for every question, so a
      // per-instance ref resets each time. Use the persistent host element as
      // the key instead — first mount per embed is skipped, every subsequent
      // mount (= user navigated) scrolls the embed into view.
      const isInitial = !initializedEmbeds.has(host);
      if (isInitial) {
        initializedEmbeds.add(host);
        // Only scroll on initial load when <amai-embed initial-scroll="true">
        if (host.getAttribute("initial-scroll") !== "true") return;
      }
      host.scrollIntoView({ block: "start" });
    } else {
      // Regular page: component stays mounted and key changes in place, so a
      // per-instance ref correctly skips only the very first render.
      if (isInitialRender.current) {
        isInitialRender.current = false;
        return;
      }
      elementRef.current.scrollIntoView({ block: "start" });
    }
  }, [key, elementRef]);

  return (
    <main
      className={clsx("survey-page", className, htmlAttributes.class)}
      ref={elementRef}
    >
      <form action="" className="survey-page__form" onSubmit={handleSave}>
        {!disablePrevious && (
          <Progress onBackButton={onPreviousPage} step={stepForPageKey(key)} />
        )}
        {(title || description) && (
          <div className="survey-page__intro">
            {title && <h1 className="survey-page__title">{title}</h1>}
            {description && <p className="survey-page__text">{description}</p>}
          </div>
        )}

        {children}

        {disablePrevious && variant && (
          <div className="answer--labels">
            <div className="answer--left-label">
              {i18n.t([
                "statement_question",
                "options",
                variant,
                "totally_disagree",
              ])}
            </div>
            <div className="answer--middle-label">
              {i18n.t(["statement_question", "options", variant, "neutral"])}
            </div>
            <div className="answer--right-label">
              {i18n.t([
                "statement_question",
                "options",
                variant,
                "totally_agree",
              ])}
            </div>
          </div>
        )}

        {!disableNext && (
          <button className="survey-page__action">
            {i18n.t("general.next_page")}
          </button>
        )}

        <div className={disableNext ? "privacy privacy--no-button" : "privacy"}>
          <a target="_new" href={i18n.t("privacy.url")}>
            {i18n.t("privacy.text")}
          </a>
        </div>
      </form>
    </main>
  );
}

export const PageComponentA = (props) => (
  <PageComponent {...props} variant="a" />
);
export const PageComponentR = (props) => (
  <PageComponent {...props} variant="r" />
);
